import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchInChunks } from '@/lib/chunkIn';
import { useSmartPolling } from './useSmartPolling';
import {
  CONNECTION_LEVEL_ERROR_CODES,
  classifyConnectionFailure,
  type ConnectionSeverity,
} from '@/lib/whatsappErrorMessages';

export interface ConnectionHealth {
  /** Retro-compat — true quando severity === 'likely_banned'. */
  isLocked: boolean;
  /** Severidade do bloqueio (heurística baseada em código + frequência). */
  severity: ConnectionSeverity;
  /** Código que mais aparece (predominante) entre as falhas — pra exibir na UI. */
  codigoPredominante: string | null;
  /** Total de falhas de nível-conexão na janela. */
  totalFalhas: number;
  /** Primeira falha registrada na janela (ISO) — pra mostrar "Primeira falha: X". */
  primeiraFalhaEm: string | null;
  /** Janela considerada (em horas). */
  windowHours: number;
  /** Mantido por retro-compat com leitura externa (não usado mais internamente). */
  failures131031: number;
}

const WINDOW_HOURS = 6;

/**
 * Saúde de uma conexão WhatsApp (linha/WABA) baseada em falhas recentes
 * dos códigos que afetam a CONEXÃO INTEIRA (131031, 368, 133000).
 *
 * Estratégia:
 * - Conta cada falha desses códigos nas últimas 6h pra essa connection_id
 * - Classifica severidade via heurística (`classifyConnectionFailure`)
 *   - 368 = restricted_temp (Meta marca como temporário por natureza)
 *   - 131031 / 133000 + frequência alta = likely_banned
 *   - 131031 / 133000 + frequência baixa = restricted_temp
 *
 * Polling: Peso C (baixa frequência), pausa em tab oculta / Economy Mode.
 * Ref.: mem://architecture/smart-polling-policy
 */
export function useConnectionHealth(connectionId: string | null | undefined) {
  const { refetchInterval } = useSmartPolling({ weight: 'C', enabled: !!connectionId });

  return useQuery<ConnectionHealth>({
    queryKey: ['whatsapp-connection-health', connectionId],
    enabled: !!connectionId,
    refetchInterval,
    staleTime: 60_000,
    queryFn: async () => {
      const sinceIso = new Date(Date.now() - WINDOW_HOURS * 60 * 60 * 1000).toISOString();
      const codes = Array.from(CONNECTION_LEVEL_ERROR_CODES);

      // Traz só erro_codigo + created_at; agregamos em memória (lista é pequena).
      // Em 2 passos (o gateway nao resolve o embed conversation:conversation_id!inner).
      // 1) mensagens com erro de conexao no window (raras -> set pequeno)
      const { data: errMsgs, error } = await supabase
        .from('whatsapp_messages')
        .select('erro_codigo, created_at, conversation_id')
        .in('erro_codigo', codes)
        .gte('created_at', sinceIso)
        .order('created_at', { ascending: true })
        .limit(500);

      if (error) {
        // Não bloquear UI por erro de telemetria
        console.warn('[useConnectionHealth] erro:', error.message);
        return {
          isLocked: false,
          severity: 'ok',
          codigoPredominante: null,
          totalFalhas: 0,
          primeiraFalhaEm: null,
          windowHours: WINDOW_HOURS,
          failures131031: 0,
        };
      }

      // 2) filtrar so as mensagens dessa conexao (resolve connection_id pela conversa)
      const convIds = Array.from(new Set((errMsgs || []).map((m: any) => m.conversation_id).filter(Boolean)));
      let okConvIds = new Set<string>();
      if (convIds.length > 0) {
        const { data: convs } = await fetchInChunks(convIds as string[], (parte) =>
          supabase
            .from('whatsapp_conversations')
            .select('id')
            .eq('connection_id', connectionId as string)
            .in('id', parte)
        );
        okConvIds = new Set((convs || []).map((c: any) => c.id));
      }
      const data = (errMsgs || []).filter((m: any) => okConvIds.has(m.conversation_id));

      const rows = (data || []) as Array<{ erro_codigo: string | null; created_at: string }>;
      const totalFalhas = rows.length;
      const primeiraFalhaEm = rows[0]?.created_at ?? null;
      const failures131031 = rows.filter(r => r.erro_codigo === '131031').length;

      // Código mais frequente entre as falhas (default 131031 se empate / vazio).
      const counts: Record<string, number> = {};
      for (const r of rows) {
        if (!r.erro_codigo) continue;
        counts[r.erro_codigo] = (counts[r.erro_codigo] || 0) + 1;
      }
      const codigoPredominante = Object.keys(counts).length > 0
        ? Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0]
        : null;

      const severity = totalFalhas === 0
        ? 'ok'
        : classifyConnectionFailure(codigoPredominante, totalFalhas, WINDOW_HOURS);

      return {
        isLocked: severity === 'likely_banned',
        severity,
        codigoPredominante,
        totalFalhas,
        primeiraFalhaEm,
        windowHours: WINDOW_HOURS,
        failures131031,
      };
    },
  });
}
